import styled, { css } from "styled-components";

import { Icon } from "_/components/icon";

export const Table = styled.table<{ $columnCount: number }>`
  display: grid;
  grid-template-columns: ${(p) => `${"auto ".repeat(p.$columnCount)} 1fr`};
  background-color: ${(p) => p.theme.background.secondary};
  border: 1px solid ${(p) => p.theme.border.default};
  border-radius: 8px;
  width: 100%;
  min-width: min-content;
`;

export const Thead = styled.thead`
  display: contents;
`;

export const Tbody = styled.tbody`
  display: contents;
`;

export const Tr = styled.tr`
  display: contents;

  &:hover:not(:only-child) > td {
    background-color: ${(p) => p.theme.background.tertiary};
  }
`;

export const Th = styled.th<{
  $variant?: "narrow" | "normal";
  $freezeHeaders?: boolean;
}>`
  ${(p) =>
    p.$freezeHeaders &&
    `
    position: sticky;
    top: 0;
  `}

  font-weight: 400;
  font-size: 12px;
  line-height: auto;
  text-align: left;
  user-select: none;
  height: 100%;

  letter-spacing: 0.2px;
  padding: ${(p) =>
    p.$variant === "narrow" ? "0.5rem 0.5rem" : "0.5rem 1rem"};
  white-space: nowrap;

  color: ${(p) => p.theme.text.muted};
  background-color: ${(p) => p.theme.background.secondary};
  border-bottom: 1px solid ${(p) => p.theme.border.default};

  &:last-of-type {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const ThCell = styled.div`
  display: flex;
  align-items: center;

  cursor: pointer;

  ${Icon} {
    font-size: 18px;
  }
`;

export const Td = styled.td.attrs({
  role: "gridcell",
})<{
  $variant?: "narrow" | "normal";
  $customCss?: ReturnType<typeof css>;
  $customCssOverrides?: ReturnType<typeof css>;
}>`
  display: flex;
  align-items: center;
  align-self: center;
  align-content: center;
  height: 100%;
  min-height: 3.5rem;

  font-weight: 400;
  font-size: 0.875rem;

  color: ${(p) => p.theme.text.default};
  border-bottom: 1px solid ${(p) => p.theme.border.default};
  padding: ${(p) =>
    p.$variant === "narrow" ? "0.5rem 0.5rem" : "0.5rem 1rem"};
  /*
    the last cell in each row is a placeholder to take up all remaining space
    without unnaturally stretching the other cells.
   */
  &:last-of-type {
    padding-left: 0;
    padding-right: 0;
  }
  ${(p) => p.$customCss ?? ""};
  ${(p) => p.$customCssOverrides ?? ""};
`;

/**
 * Component to use for table cells that may contain clickable content, as
 * opposed to the entire row being clickable.
 *
 * Most tables in Basis have the entire row clickable, but some tables only have
 * some cells clickable. For the cells that are links, we make the entire cell
 * into a link target (which makes the link easier to click on and more
 * discoverable via the hover underline. In order to make the whole cell
 * clickable and hover-able, we must remove the padding on the parent TD and
 * apply padding to the cell content instead.
 */
export const CustomTd = styled(Td)`
  padding: 0;
  min-height: 1rem;
  align-items: stretch;
`;

export const CustomTdContent = styled.div`
  align-content: center;
  padding: 0.625rem 1rem;
  height: 100%;
  text-decoration: inherit;
`;

/**
 * A cell spanning every column of the table, including the trailing gutter.
 * Rows are `display: contents`, so a row's single `FullWidthTd` fills the
 * whole grid row. Used for the no-data placeholder and for content that
 * expands beneath a data row, e.g. accordion-style detail sections.
 */
export const FullWidthTd = styled(Td)`
  display: grid;
  grid-column: 1 / -1;
`;

/** Row holding expanded content beneath a data row; not highlighted on hover. */
export const ExpandedTr = styled(Tr)`
  &:hover:not(:only-child) > td {
    background-color: transparent;
  }
`;

export const NoData = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0.75rem 1rem;
  color: ${(p) => p.theme.text.muted};
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 0.75rem;
`;

export const PaginationPage = styled.span`
  min-width: 4rem;
  color: ${(p) => p.theme.text.muted};
  font-size: 0.875rem;
  text-align: center;
`;

export const ScrollShadowFrame = styled.div<{ $freezeHeaders?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  ${(p) => p.$freezeHeaders && "flex: 1; min-height: 0;"}
`;

/*
  The shadows are positioned against the frame, whose box includes the scroll
  container's scrollbars. Insetting by the scrollbar sizes keeps the shadows
  the same height as the table and clear of a vertical scrollbar; the extra
  1px stays inside the table border.
*/
export const EdgeShadow = styled.div<{
  $side: "left" | "right";
  $visible: boolean;
  $scrollbarWidth: number;
  $scrollbarHeight: number;
}>`
  position: absolute;
  top: 1px;
  bottom: ${(p) => 1 + p.$scrollbarHeight}px;
  width: 16px;
  pointer-events: none;
  z-index: 1;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 150ms ease;

  ${(p) =>
    p.$side === "left"
      ? css`
          left: 1px;
          border-radius: 7px 0 0 7px;
          background: linear-gradient(
            to right,
            rgba(0, 0, 0, 0.28),
            transparent
          );
        `
      : css`
          right: ${1 + p.$scrollbarWidth}px;
          border-radius: 0 7px 7px 0;
          background: linear-gradient(
            to left,
            rgba(0, 0, 0, 0.28),
            transparent
          );
        `}
`;

// Infuriatingly, a wrapper element is required to get height:100% grid cells to
// work properly in Safari. See https://stackoverflow.com/a/63197330 It doesn't
// seem to matter if it's a flex or grid container, as long as it's there.
export const TableContainer = styled.div<{ $freezeHeaders?: boolean }>`
  ${(p) =>
    p.$freezeHeaders
      ? `
          flex: 1;
          overflow-y: auto;
          scrollbar-gutter: stable;
          scrollbar-width: thin;
          scrollbar-color:  ${p.theme.scrollbar.thumb.default} ${p.theme.scrollbar.track};
          &::-webkit-scrollbar {
            scrollbar-color:  ${p.theme.scrollbar.thumb.default} ${p.theme.scrollbar.track};
            background-color: ${p.theme.scrollbar.track};
          }
          &::-webkit-scrollbar-thumb {
            opacity: 0;
            background-color: ${p.theme.scrollbar.thumb.default};
            &:hover {
              background-color: ${p.theme.scrollbar.thumb.hover};
            }
            &:active {
              background-color: ${p.theme.scrollbar.thumb.active};
            }
          }
        `
      : ``}

  /* Non-wrapping cells can make the table wider than its container. */
  overflow-x: auto;
`;

export const EmptyState = styled.div`
  padding-left: 1rem;
`;
